<script setup lang="ts">
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { i18n } from '@/i18n';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import InventoryDashboardChildLayout from '../InventoryDashboardChildLayout.vue';
import { DetailAlertData } from '../useInventoryDashboardStore/types';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { LoadingStatus } from '@/corePagesModule/components/ezTable/types';
import { InventoryDashboardViewModel } from '../inventoryDashboardViewModel';
import { DETAIL_HEADER_OPTION, PAGE_CONFIG, PageType } from '@/inventoryDashboardModule/constants';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import { useInventoryDashboardStore } from '../useInventoryDashboardStore';
import ArrowIcon from '@/components/atom/ArrowIcon.vue';
import RowCheckBox from '@/components/atom/RowCheckBox.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import ErrorMessage from '@/corePagesModule/components/ErrorMessage.vue';
import AlertInputSharp from '@/inventoryDashboardModule/components/alertInput/AlertInputSharp.vue';
import AlertInputConsecutive from '@/inventoryDashboardModule/components/alertInput/AlertInputConsecutive.vue';
import { diContainer } from '@/coreContainer/container';
import {
  DashboardAlertSetting,
  DashboardAlertSettingsValueType,
  dashboardAlertSettingValidation,
  DashboardAlertSeverity,
} from '@/inventoryDashboardModule/domains/entities/DashboardAlert';
import { DashboardAlertSettingFactory } from '@/inventoryDashboardModule/domains/factories/DashboardAlertSettingFactory';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import DashboardAlertBadge from '@/inventoryDashboardModule/components/DashboardAlertBadge.vue';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { useTableHeader } from '@/corePagesModule/composables/useTableHeader';
import { HeaderList } from '@/coreDomainsModule/table';
import TableHeaderModifyButton from '@/corePagesModule/components/TableHeaderModifyButton.vue';

const t = i18n.t;
const route = useRoute();

const store = useInventoryDashboardStore();
const { getDetailAggregateBusinessWithAlertData } = store;
const vm = new InventoryDashboardViewModel();

/**
 * detailAlertData初期化
 */
const initDetailAlertData = (): DetailAlertData => ({
  overview: {
    excess: 0,
    shortage: 0,
    unresolved: {
      unmatched: 0,
      noOrderNumber: 0,
      deadline: 0,
    },
  },
  businesses: [],
});

const viewType = ref<ViewType>('none');
const loadingStatus = ref<LoadingStatus>({ type: 'loading' });
const sortType = ref<'ASC' | 'DESC'>('DESC');
const sortTarget = ref<string | undefined>(undefined);
const pagingOptions = ref<IPagingOptions>(initPagingOptions(30));
const filterItems = ref<FilterContentItem[]>([]);
const checkedBusinessId = ref<string[]>([]);

const detailAlertData = ref<DetailAlertData>(initDetailAlertData());
const alertSettings = ref<DashboardAlertSetting>(DashboardAlertSettingFactory.create());
const isDisplaySetting = ref<boolean>(false);
const errorMessage = ref<string>('');

const ViewType = ['excess', 'shortage', 'unresolved', 'none'] as const;
/**
 * アラート詳細画面で扱う表示カテゴリ
 */
type ViewType = (typeof ViewType)[number];

const displayViewTypeList = computed((): ViewType[] =>
  pageType.value === PageType.Critical ? ['excess', 'shortage'] : ['excess', 'shortage', 'unresolved']
);

const loadingStatusWithView = computed((): LoadingStatus => {
  if (loadingStatus.value.type === 'success') {
    return { type: detailAlertData.value.businesses.length === 0 ? 'zero-match' : 'success' };
  }
  return loadingStatus.value;
});

const pageType = computed((): Extract<PageType, 'critical' | 'warning'> => {
  const severity = route.query.severity;
  if (!severity) throw new Error();
  switch (severity) {
    case 'warning':
      return PageType.Warning;
    case 'critical':
      return PageType.Critical;
  }
  throw new Error();
});

const pageConfig = computed(() => {
  if (!pageType.value) return { title: 'エラー', desc: '' };
  return PAGE_CONFIG[pageType.value] ?? { title: 'エラー', desc: '' };
});

const headerOption = computed(() => DETAIL_HEADER_OPTION[pageType.value]);
const fullHeaderKeys = headerOption.value.map((h) => h.key);
const { headerList, updateDisplayHeader } = useTableHeader(fullHeaderKeys, 'inventoryDashboardAlert', []);

const modifiableHeaders = computed(
  (): HeaderList =>
    headerOption.value
      .filter((h) => !['checkbox', 'detail', 'headerModify'].includes(h.key))
      .map((h) => ({
        key: h.key,
        text: vm.convertHeaderKeyToDisplayValue(h.key),
        isShown: headerList.value.includes(h.key),
      }))
);
const gridTemplateColumns = computed(() =>
  headerOption.value.filter((header) => headerList.value.includes(header.key)).map((h) => `minmax(${h.width}px, ${h.width}fr)`)
);

const settingSectionDesc = computed(() =>
  viewType.value === 'excess'
    ? {
        reason1: 'excess_forecast_consecutive_low',
        reason2: pageType.value + '_excess_forecast_sharp_decrease',
        reason1Text: 'dashboard:未満',
        reason2Text: 'dashboard:以下',
      }
    : {
        reason1: 'shortage_forecast_consecutive_high',
        reason2: pageType.value + '_shortage_forecast_sharp_increase',
        reason1Text: 'dashboard:より高い',
        reason2Text: 'dashboard:以上',
      }
);

const alertFilterItems = computed(() => {
  switch (viewType.value) {
    case 'excess':
      return ['excess_forecast_sharp_decrease', 'excess_forecast_consecutive_low'];
    case 'shortage':
      const res = ['shortage_forecast_sharp_increase', 'shortage_forecast_consecutive_high'];
      if (pageType.value === 'critical') res.push('shortage_out_of_stock');
      return res;
    default:
      return [
        'excess_forecast_sharp_decrease',
        'excess_forecast_consecutive_low',
        'shortage_forecast_sharp_increase',
        'shortage_forecast_consecutive_high',
        'shortage_out_of_stock',
      ];
  }
});

const alertFilterInfo = ref<string[]>(alertFilterItems.value);

/**
 * Refリセット
 */
const $reset = () => {
  loadingStatus.value = { type: 'loading' };
  sortType.value = 'DESC';
  sortTarget.value = undefined;
  pagingOptions.value = initPagingOptions(30);
  filterItems.value = [];
  detailAlertData.value = initDetailAlertData();
  alertSettings.value = DashboardAlertSettingFactory.create();
  isDisplaySetting.value = false;
  errorMessage.value = '';
};

/**
 * 初期フェッチ(設定込み)
 */
const initFetch = async () => {
  $reset();
  const res = await waitLoading(
    Promise.all([
      getDetailAggregateBusinessWithAlertData(
        pageType.value === PageType.Critical ? 'critical' : 'warning',
        viewType.value === 'excess' ? 'excess' : 'shortage',
        sortType.value,
        sortTarget.value,
        pagingOptions.value.pageSize,
        pagingOptions.value.offset,
        filterItems.value,
        alertFilterInfo.value,
        undefined
      ),
      (async () => {
        const res = await diContainer.getDashboardAlertSettingUseCase.execute();
        switch (res.status.type) {
          case 'success':
            return res.status.data;
          default:
            return null;
        }
      })(),
    ]),
    undefined,
    ''
  );
  if (!res || !res[0] || !res[1]) {
    showInfoModal({
      icon: 'error',
      modalTitle: t('取得に失敗しました'),
      modalDescription: t('情報の取得に失敗しました。リロードしてください。'),
      backButtonLabel: 'OK',
      isDisabledNextButton: true,
    });
    loadingStatus.value.type = 'error';
    return;
  }
  detailAlertData.value = res[0][0];
  pagingOptions.value = res[0][1];
  alertSettings.value = res[1];
  loadingStatus.value.type = 'success';
};

/**
 * データのみフェッチ
 */
const fetch = async () => {
  loadingStatus.value.type = 'loading';
  const severity = pageType.value === PageType.Critical ? 'critical' : 'warning';
  const res = await getDetailAggregateBusinessWithAlertData(
    severity,
    viewType.value === 'excess' ? 'excess' : 'shortage',
    sortType.value,
    sortTarget.value,
    pagingOptions.value.pageSize,
    pagingOptions.value.offset,
    filterItems.value,
    alertFilterInfo.value,
    alertSettings.value[severity]
  );
  if (!res) {
    showInfoModal({
      icon: 'error',
      modalTitle: t('取得に失敗しました'),
      modalDescription: t('情報の取得に失敗しました。リロードしてください。'),
      backButtonLabel: 'OK',
      isDisabledNextButton: true,
    });
    loadingStatus.value.type = 'error';
    return;
  }
  detailAlertData.value = res[0];
  pagingOptions.value = res[1];
  loadingStatus.value.type = 'success';
};

/**
 * 表示カテゴリの切り替えハンドラ。
 * @param changedType - 切り替え先の表示カテゴリ
 */
const onClickTypeButton = async (changedType: ViewType) => {
  checkedBusinessId.value = [];
  viewType.value = changedType;
  await fetch();
};

/**
 * ページング変更時のハンドラ
 * @param option - pagingOption
 */
const onChangePagingHandler = async (option: IPagingOptions) => {
  pagingOptions.value = { ...option };
  await fetch();
};

/**
 * フィルター適用時のハンドラ
 * @param changedFilterItems - 変更したフィルタ情報
 */
const onApplyFilterHandler = async (changedFilterItems: FilterContentItem[]) => {
  filterItems.value = [...changedFilterItems];
  await fetch();
};

/**
 * ヘッダーキーをソート用のキーに変換する
 * @param headerKey - ヘッダーキー
 */
const convertHeaderKeyToSortKey = (headerKey: string) => {
  if (headerKey === 'reason') return '';
  return headerKey;
};

/**
 * ソートの矢印を押した時のハンドラ
 * @param headerKey - ヘッダーキー
 */
const onClickSortArrowHandler = async (headerKey: string) => {
  const sortKey = convertHeaderKeyToSortKey(headerKey);
  if (sortTarget.value === sortKey) {
    sortType.value = sortType.value === 'ASC' ? 'DESC' : 'ASC';
  } else {
    sortTarget.value = sortKey;
    sortType.value = 'ASC';
  }
  pagingOptions.value = initPagingOptions(pagingOptions.value.pageSize);
  await fetch();
};

/**
 * 設定変更のinputイベント
 * @param e - イベント
 * @param key - イベントが発生したinputのキー
 */
const onInput = (e: Event, key: DashboardAlertSettingsValueType) => {
  const value = (e.target as HTMLInputElement).value;
  alertSettings.value[pageType.value][viewType.value][key] = value;
  errorMessage.value = dashboardAlertSettingValidation(value, key);
};

/**
 * 設定の表示非表示切り替え
 */
const changeDisplaySetting = () => {
  isDisplaySetting.value = !isDisplaySetting.value;
};

/**
 * アラートのフィルタボタンを押した際の処理
 * @param value - 押下したボタン値
 */
const onClickAlertFilterButton = (value: string) => {
  if (alertFilterInfo.value.includes(value)) {
    alertFilterInfo.value = alertFilterInfo.value.filter((a) => a !== value);
  } else {
    alertFilterInfo.value.push(value);
  }
  fetch();
};

/**
 * チェックされたかどうか
 * @param businessId - 対象の取引ID
 * @param isChecked - チェック済みかどうか
 */
const onCheckedRow = (businessId: string, isChecked: boolean) => {
  if (isChecked && !checkedBusinessId.value.includes(businessId)) {
    checkedBusinessId.value.push(businessId);
  } else if (!isChecked && checkedBusinessId.value.includes(businessId)) {
    checkedBusinessId.value = checkedBusinessId.value.filter((b) => b !== businessId);
  }
};

/**
 * ヘッダーを切り替える処理
 * @param headerKey - ヘッダーキー
 * @param isHide - 隠すかどうか
 */
const onToggleHeader = async (headerKey: string, isHide: boolean) => {
  await updateDisplayHeader(headerKey, isHide);
};

onMounted(async () => await initFetch());
onUnmounted(() => {
  viewType.value = 'none';
  $reset();
});
</script>
<template>
  <InventoryDashboardChildLayout>
    <div class="body">
      <div class="body__header">
        <div class="body__header__title">
          <Mdi
            v-if="pageConfig.icon"
            :icon="pageConfig.icon.name"
            :variant="(pageConfig.icon.variant as any)"
            :style="pageConfig.icon.style"
          />
          <div class="title--text">{{ t(pageConfig.title) }}</div>
        </div>
        <div class="body__header__desc">{{ pageConfig.desc && t(pageConfig.desc) }}</div>
      </div>
      <div class="type-list">
        <button
          v-for="key in displayViewTypeList"
          :key="key"
          class="type-list__button"
          :class="{ 'type-list__button--active': viewType === key }"
          @click="onClickTypeButton(key)"
        >
          <div class="type-list__button__text--title">{{ t(`dashboard:${pageType}_${key}`) }}</div>
          <div class="type-list__button__value">
            <div class="type-list__button__text--value">
              {{
                key === 'unresolved'
                  ? Object.keys(detailAlertData.overview[key]).reduce((acc, k) => (acc += detailAlertData.overview[key][k]), 0)
                  : detailAlertData.overview[key]
              }}
            </div>
            <div class="type-list__button__text--unit">{{ t('件') }}</div>
          </div>
          <div class="type-list__button__icon"><Mdi class="type-list__button__text--title" icon="arrow_forward" /></div>
        </button>
      </div>
      <div class="main">
        <div v-if="viewType === 'none'">
          {{ t('項目をクリックすると詳細情報を表示します') }}
        </div>
        <div v-else-if="viewType === 'unresolved'" class="unresolved">
          <div class="title--text">{{ t('未対応の内訳') }}</div>
          <div class="unresolved-button__container">
            <RouterLink
              v-for="(value, type) in detailAlertData.overview.unresolved"
              :key="type"
              :to="type === 'unmatched' ? '/unmatched-orders' : '/firm-order'"
              target="_blank"
            >
              <div class="unresolved-button">
                <div>
                  <div class="unresolved-button__title">
                    {{ t(`dashboard:${type}`) }}
                  </div>
                  <div class="unresolved-button__value">{{ value + t('件') }}</div>
                </div>
                <div class="next-icon"><Mdi class="title__text" icon="chevron_right" /></div>
              </div>
            </RouterLink>
          </div>
        </div>
        <template v-else>
          <div class="main__header">
            <div class="main__header__title">{{ t(`dashboard:${pageType}_${viewType}`) }}</div>
            <div class="main__header__desc">
              {{ t('以下の条件に該当する在庫アラートを表示しています。') }}
            </div>
          </div>
          <div class="main__setting">
            <button class="setting__header" @click="changeDisplaySetting">
              <div class="setting__header__label">
                <div class="setting__header__label__title">{{ t('条件設定と実行') }}</div>
                <template v-if="!isDisplaySetting">
                  <div>
                    {{ t(settingSectionDesc.reason1) }}:
                    <span class="text-bold">
                      {{
                        `${t('dashboard:ヶ月連続で', {
                          input: alertSettings[pageType][viewType].consecutiveMonths,
                        })}`
                      }}
                      {{
                        ` ${t(settingSectionDesc.reason1Text, {
                          input: `${alertSettings[pageType][viewType].ratioThreshold} %`,
                        })}`
                      }}
                    </span>
                  </div>
                  <div>
                    {{ t(settingSectionDesc.reason2) }}:
                    <span class="text-bold">
                      {{
                        `${t('前月比')} ${alertSettings[pageType][viewType].momChangeRateThreshold} % ${t(settingSectionDesc.reason2Text)}`
                      }}
                      {{ t('かつ') }}
                      {{ `${t('数量')} ${alertSettings[pageType][viewType].minQuantityThreshold} ${t('以上')}` }}
                    </span>
                  </div>
                </template>
                <template v-else-if="errorMessage !== ''">
                  <ErrorMessage is-error :error-message="errorMessage" />
                </template>
              </div>
              <div>
                <Mdi :icon="isDisplaySetting ? 'expand_less' : 'expand_more'" />
              </div>
            </button>
            <template v-if="isDisplaySetting">
              <div class="setting__inputs">
                <div class="input__wrap">
                  <AlertInputConsecutive
                    :direction="viewType"
                    :consecutive-months="alertSettings[pageType as DashboardAlertSeverity][viewType].consecutiveMonths"
                    :ratio-threshold="alertSettings[pageType as DashboardAlertSeverity][viewType].ratioThreshold"
                    @update:consecutive-months="(e) => onInput(e, 'consecutiveMonths')"
                    @update:ratio-threshold="(e) => onInput(e, 'ratioThreshold')"
                  />
                </div>
                <div class="input__wrap">
                  <AlertInputSharp
                    :severity="pageType"
                    :direction="viewType"
                    :mom-change-rate-threshold="alertSettings[pageType as DashboardAlertSeverity][viewType].momChangeRateThreshold"
                    :min-quantity-threshold="alertSettings[pageType as DashboardAlertSeverity][viewType].minQuantityThreshold"
                    @update:mom-change-rate-threshold="(e) => onInput(e, 'momChangeRateThreshold')"
                    @update:min-quantity-threshold="(e) => onInput(e, 'minQuantityThreshold')"
                  />
                </div>
              </div>
              <div class="setting__control">
                <TextButton :disabled="errorMessage !== ''" size="btn-sm" class="setting__control__confirm" @click="fetch">
                  {{ t('実行') }}
                </TextButton>
                <TextButton button-style="btn-secondary" size="btn-sm" class="setting__control__confirm" @click="initFetch">
                  {{ t('リセット') }}
                </TextButton>
                <RouterLink to="/settings/logic/dashboard-alert">
                  <div class="setting__control__link">{{ t('詳細設定ページへ') }}</div>
                </RouterLink>
              </div>
            </template>
          </div>
          <div class="main__control">
            <div class="main__control__filter">
              <FilterModalButton
                filter-modal-type="inventoryDashboardDetailAgg"
                :select-input-index="10"
                :filter-items="filterItems"
                @apply="onApplyFilterHandler"
              />
              <div class="main__control__alert-filter">
                <button
                  v-for="alert in alertFilterItems"
                  :key="alert"
                  :class="{ 'main__control__alert-filter__button--off': !alertFilterInfo.includes(alert) }"
                  @click="() => onClickAlertFilterButton(alert)"
                >
                  <DashboardAlertBadge
                    :severity="pageType"
                    :alert="alert"
                    :with-icon-name="alertFilterInfo.includes(alert) ? 'visibility' : 'visibility_off'"
                  />
                </button>
              </div>
            </div>

            <Pagination
              :paging-options="pagingOptions ?? initPagingOptions(0)"
              :is-loading="loadingStatus.type === 'loading'"
              no-bottom-margin
              @on-change-page="onChangePagingHandler($event)"
            />
          </div>
          <div class="main__table base-table-wrapper">
            <EzTable
              :grid-template-columns="gridTemplateColumns"
              :loading-status="loadingStatusWithView"
              sticky-header
              is-vertical-scrollable
            >
              <template #header>
                <template v-for="(header, index) in headerList.slice(0, -1)" :key="index">
                  <EzHeader v-if="header === 'checkbox'" class="main__table__header"><RowCheckBox /></EzHeader>
                  <EzHeader v-else-if="header === 'detail'" class="main__table__header" />
                  <EzHeader
                    v-else
                    :content-padding="header === 'detail' ? 0 : undefined"
                    :force-center="header === 'detail'"
                    class="main__table__header"
                  >
                    <div class="main__table__header__inner">
                      {{ vm.convertHeaderKeyToDisplayValue(header) }}
                      <ArrowIcon
                        :asc="convertHeaderKeyToSortKey(header) === sortTarget ? sortType === 'ASC' : false"
                        class="main__table__header__icon"
                        @on-click="() => onClickSortArrowHandler(header)"
                      />
                    </div>
                  </EzHeader>
                </template>
                <EzHeader sticky="right">
                  <TableHeaderModifyButton :modifiable-header-list="modifiableHeaders" @toggle="onToggleHeader" />
                </EzHeader>
              </template>
              <template #body>
                <EzTableRow v-for="(data, index1) in detailAlertData.businesses" :key="index1" :grid-template-columns="gridTemplateColumns">
                  <template v-for="(header, index2) in headerList.slice(0, -1)" :key="`inner-${index2}`">
                    <EzCell
                      v-if="header === 'checkbox'"
                      :is-highlighted="checkedBusinessId.includes(data.businessId)"
                      class="main__table__cell"
                    >
                      <RowCheckBox @change-status="(isChecked) => onCheckedRow(data.businessId, isChecked)" />
                    </EzCell>
                    <EzCell
                      v-else-if="header === 'detail'"
                      :content-padding="0"
                      :is-highlighted="checkedBusinessId.includes(data.businessId)"
                      class="main__table__cell"
                    >
                      <RouterLink v-if="data.businessId" :to="{ name: 'recommend-order', params: { id: data.businessId } }" target="_blank">
                        <TextButton class="table-detail-button" button-style="btn-secondary" :on-click="() => {}">
                          {{ t('詳細') }}
                        </TextButton>
                      </RouterLink>
                    </EzCell>
                    <EzCell
                      v-else-if="
                        [
                          'aggregateBusinessResponsibleUsers',
                          'baseBusinessResponsibleUsers',
                          'forecastForReceiptResponsibleUsers',
                        ].includes(header)
                      "
                      :is-highlighted="checkedBusinessId.includes(data.businessId)"
                      class="main__table__cell"
                    >
                      {{ vm.convertUserAccountToDisplayValue(data[header]) }}
                    </EzCell>
                    <EzCell
                      v-else-if="header === 'reason'"
                      :is-highlighted="checkedBusinessId.includes(data.businessId)"
                      class="main__table__cell"
                    >
                      <div class="main__table__cell--alert">
                        <template v-for="alert in data['alertType']" :key="index1 + alert">
                          <DashboardAlertBadge :alert="alert" :severity="pageType" />
                        </template>
                      </div>
                    </EzCell>
                    <EzCell
                      v-else-if="vm.numberHeaderKeys.includes(header)"
                      is-number
                      :is-highlighted="checkedBusinessId.includes(data.businessId)"
                      class="main__table__cell"
                    >
                      {{ vm.convertNumberStringToDisplayValue(header, data[header]) }}
                    </EzCell>
                    <EzCell v-else :is-highlighted="checkedBusinessId.includes(data.businessId)" class="main__table__cell">
                      {{ data[header] ?? '--' }}
                    </EzCell>
                  </template>
                  <EzCell />
                </EzTableRow>
              </template>
            </EzTable>
          </div>
        </template>
      </div>
    </div>
  </InventoryDashboardChildLayout>
</template>
<style lang="scss" scoped>
.body {
  display: grid;
  padding: 8px 16px 24px 16px;
  gap: 16px;
  min-width: 0;
  overflow: hidden;

  &__header {
    display: grid;
    gap: 4px;
    &__title {
      display: grid;
      grid-template-columns: max-content max-content;
      gap: 4px;
      align-items: center;
    }
  }
}
.type-list {
  display: grid;
  grid-template-columns: v-bind('`repeat(${displayViewTypeList.length}, minmax(0, 1fr))`');
  gap: 32px;
  &__button {
    padding: 8px 16px;
    height: 64px;
    width: 100%;
    display: grid;
    grid-template-columns: 1.5fr repeat(2, minmax(0, 1fr));
    gap: 8px;
    align-items: center;
    background-color: $semanticWhite;
    border: 1px solid $borderDefault;
    border-radius: 8px;
    box-shadow: 0 0 22px 0px rgba(128, 128, 128, 0.4);
    transition: box-shadow 0.2s ease, transform 0.2s ease;
    &--active {
      background-color: $semanticPrimarySubdued;
    }
    &__value {
      display: grid;
      grid-template-columns: max-content max-content;
      gap: 4px;
      align-items: end;
    }
    &__icon {
      justify-self: end;
    }
    &__text {
      &--title {
        font-weight: 700;
        font-size: 22px;
        line-height: 1.3;
        color: $textSecondary;
        min-width: 0;
        white-space: normal;
        overflow-wrap: break-word;
        word-break: break-word;
      }
      &--value {
        font-weight: 700;
        font-size: 32px;
        line-height: 1.3;
        color: $textDefault;
      }
      &--unit {
        font-weight: 400;
        font-size: 22px;
        line-height: 1.3;
        color: $textDefault;
      }
    }
  }
}

.main {
  display: grid;
  padding: 16px 16px 24px 16px;
  border-radius: 8px;
  background-color: $semanticWhite;
  border: 1px solid $borderDefault;
  gap: 16px;
  min-width: 0;
  overflow: hidden;

  &__header {
    &__title {
      font-weight: 700;
      font-size: 22px;
      line-height: 1.3;
    }
  }

  &__setting {
    display: grid;
    background-color: $semanticPrimarySubdued;
    border-radius: 8px;
    padding: 16px;
    gap: 16px;
  }

  .setting {
    &__header {
      display: grid;
      grid-template-columns: max-content max-content;
      justify-content: space-between;

      &__label {
        display: grid;
        grid-template-columns: max-content max-content max-content;
        gap: 16px;
        align-items: baseline;
        &__title {
          font-size: 16px;
          font-weight: 700;
        }
      }
    }

    &__inputs {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }
    &__control {
      display: grid;
      grid-template-columns: max-content max-content max-content;
      align-items: center;
      gap: 8px;
      &__link {
        color: $semanticPrimary;
      }
    }
  }

  &__control {
    &__filter {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, max-content));
      gap: 8px;
    }
    &__alert-filter {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, max-content));
      gap: 4px;
      &__button {
        &--off {
          opacity: 0.4;
        }
      }
    }
  }

  &__table {
    overflow-x: auto;
    :deep(.table__header-container) {
      width: 100%;
    }
    &__header {
      width: 100%;
      height: 36px;
      font-weight: 700;
      font-size: 14px;
      line-height: 1.5;
      &__inner {
        width: 100%;
        display: grid;
        grid-template-columns: max-content max-content;
        justify-content: space-between;
        align-items: center;
      }
      &__icon {
        width: 18px;
        height: 18px;
      }
    }
    &__cell {
      height: 24px;
      font-weight: 400;
      font-size: 12px;
      line-height: 1.5;
      &--alert {
        display: grid;
        grid-auto-flow: column;
        grid-auto-columns: max-content;
      }
    }
  }
}

.title--text {
  font-weight: 700;
  font-size: 16px;
  line-height: 1;
}

.unresolved {
  display: grid;
  gap: 16px;
  &-button {
    display: grid;
    grid-template-columns: minmax(0, 1fr) max-content;
    justify-content: space-between;
    gap: 4px;
    background-color: $backgroundDefault;
    border-radius: 4px;
    padding: 12px;
    justify-content: start;
    align-items: center;
    &__container {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 16px;
    }
    &__title {
      text-align: start;
      font-size: 14px;
      color: $textSecondary;
    }
    &__value {
      text-align: start;
      font-weight: 900;
      font-size: 20px;
      line-height: 1.3;
    }
  }
}

.table-detail-button {
  display: grid;
  justify-content: center;
  align-items: center;
  font-family: Noto Sans JP;
  padding: unset;
  height: 16px;
  width: 56px;
  background-color: $semanticWhite;
  color: $semanticPrimary;
  border: 1px solid $semanticPrimary;
  font-size: 12px;
  line-height: 12px;
  font-weight: 700;
}

a {
  &:hover,
  &:focus,
  &:focus-visible {
    color: inherit !important;
    background-color: inherit !important;
  }
}

.input__wrap {
  background-color: $semanticWhite;
  border-radius: 4px;
}

.text-bold {
  font-weight: 700;
}
</style>
